import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

function readSignedOut() {
  if (typeof document === 'undefined') return false
  return document.cookie.split('; ').some(c => c === 'yokan_signedout=1')
}

// Auto-redirect into SSO when there's an active YOKAN session anywhere in
// this browser. The `yokan_active` cookie is set on Domain=yokan.io by every
// RP's auth middleware while the user has a valid session, and cleared on
// /auth/logout. Browser-session cookie (no maxAge) → dies on browser close,
// so fresh post-restart visits still see the explicit button. This brings
// the dashboard back in line with the other RPs while honouring the
// signed-in/signed-out boundary intent originally introduced in 771d625.
function hasActiveYokanSession() {
  if (typeof document === 'undefined') return false
  return document.cookie.split('; ').some(c => c === 'yokan_active=1')
}

const features = [
  { icon: 'thermostat', label: 'Climate' },
  { icon: 'solar_power', label: 'Energy' },
  { icon: 'directions_car', label: 'Vehicles' },
  { icon: 'calendar_month', label: 'Calendar' },
  { icon: 'blinds', label: 'Shutters' },
  { icon: 'local_laundry_service', label: 'Appliances' },
]

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] } },
}

const fadeIn = {
  hidden: { opacity: 0, scale: 0.97 },
  show:   { opacity: 1, scale: 1, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } },
}

export default function LoginPage() {
  const [signedOut] = useState(readSignedOut)

  useEffect(() => {
    if (signedOut) return
    if (!hasActiveYokanSession()) return
    const redirect = window.location.pathname + window.location.search + window.location.hash
    const url = redirect && redirect !== '/' ? `/auth/login?redirect=${encodeURIComponent(redirect)}` : '/auth/login'
    window.location.replace(url)
  }, [signedOut])

  function signIn() {
    const redirect = window.location.pathname + window.location.search + window.location.hash
    const url = redirect && redirect !== '/' ? `/auth/login?redirect=${encodeURIComponent(redirect)}` : '/auth/login'
    window.location.assign(url)
  }

  return (
    <div className="relative min-h-screen flex overflow-hidden bg-[#131313]">

      {/* ── Hero image — left / full bleed ─────────────────────────────── */}
      <div className="absolute inset-0 lg:relative lg:flex-1">
        <img
          src="/images/yokan_family_sunrise.webp"
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-center"
        />
        {/* gradient: transparent centre → dark edges */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#131313] via-transparent to-[#131313] lg:to-[#131313]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#131313] via-transparent to-[#131313]/60" />

        {/* ambient glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{ opacity: [0.08, 0.16, 0.08] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{ background: 'radial-gradient(ellipse at 40% 60%, rgba(176,206,174,0.25) 0%, transparent 65%)' }}
        />

        {/* hero text — only visible on large screens */}
        <div className="absolute inset-0 hidden lg:flex flex-col justify-end p-16 pb-20">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.18em] mb-4" style={{ color: '#b0ceae', fontFamily: 'Plus Jakarta Sans' }}>
              YOKAN
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="text-5xl font-bold leading-tight mb-5 text-white"
              style={{ fontFamily: 'Plus Jakarta Sans' }}
            >
              Your home,<br />perfectly in view.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base max-w-sm leading-relaxed mb-8 text-outline">
              A personal dashboard connecting your smart home — energy, climate, vehicles and more — in one place.
            </motion.p>

            {/* feature chips */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
              {features.map(f => (
                <div
                  key={f.label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(8px)',
                    color: '#c3c8bf',
                    fontFamily: 'Plus Jakarta Sans',
                    letterSpacing: '0.04em',
                  }}
                >
                  <span className="material-symbols-outlined text-sm" style={{ color: '#b0ceae', fontSize: 15 }}>{f.icon}</span>
                  {f.label}
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── Login panel — right ─────────────────────────────────────────── */}
      <div className="relative z-10 flex items-center justify-center w-full lg:w-auto lg:min-w-[420px] lg:max-w-[480px] p-6">
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="show"
          className="w-full max-w-sm rounded-[2rem] p-8 flex flex-col gap-6"
          style={{
            background: 'rgba(31,32,31,0.82)',
            backdropFilter: 'blur(32px)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(176,206,174,0.04)',
          }}
        >
          {/* logo mark */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5 mb-1">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #b0ceae 0%, #6f9e6d 100%)' }}
              >
                <span className="material-symbols-outlined text-[#1d361f]" style={{ fontSize: 17 }}>cottage</span>
              </div>
              <span className="font-semibold text-white text-sm" style={{ fontFamily: 'Plus Jakarta Sans' }}>YOKAN</span>
            </div>
            <h2 className="text-2xl font-bold text-white leading-snug" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {signedOut ? 'Signed out' : 'Welcome back'}
            </h2>
            <p className="text-sm leading-relaxed text-outline">
              {signedOut ? 'You are signed out. Click below when you’re ready.' : 'Sign in to continue to your dashboard.'}
            </p>
          </div>

          {/* divider */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

          <button
            onClick={signIn}
            className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-2xl text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              background: 'linear-gradient(135deg, #b0ceae 0%, #6f9e6d 100%)',
              color: '#1d361f',
              fontFamily: 'Plus Jakarta Sans',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Sign in with Google
          </button>

          {/* feature chips — visible on mobile only */}
          <div className="flex flex-wrap gap-1.5 lg:hidden">
            {features.map(f => (
              <div
                key={f.label}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  color: '#8d928a',
                  fontFamily: 'Plus Jakarta Sans',
                }}
              >
                <span className="material-symbols-outlined" style={{ color: '#b0ceae', fontSize: 13 }}>{f.icon}</span>
                {f.label}
              </div>
            ))}
          </div>

          <p className="text-xs text-center" style={{ color: '#4a4e49' }}>
            Private dashboard — access is restricted.
          </p>
        </motion.div>
      </div>

    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  )
}
